import ListTeamMembers from '@/graphql/queries/ListTeamMembers'
import CreateTeamMember from '@/graphql/mutations/CreateTeamMember'
import DeleteTeamMember from '@/graphql/mutations/DeleteTeamMember'
import UpdateProjectOwner from '@/graphql/mutations/UpdateProjectOwner'
import ConvertInvitesToTeamMembers from '@/graphql/mutations/ConvertInvitesToTeamMembers'
import OnCreateTeamMember from '@/graphql/subscriptions/OnCreateTeamMember'
import OnUpdateTeamMemberLastLogin from '@/graphql/subscriptions/OnUpdateTeamMemberLastLogin'
import OnDeleteTeamMember from '@/graphql/subscriptions/OnDeleteTeamMember'
import OnUpdateProjectOwner from '@/graphql/subscriptions/OnUpdateProjectOwner'
import client from '@/graphql/client'
import { addToSubscriptions } from '@/api/subscriptionHelpers'

export const teamMembers = {
  namespaced: true,
  state: {
    teamMembers: null,
    subscriptions: null
  },
  mutations: {
    setTeamMembers (state, payload) {
      state.teamMembers = payload
    },
    setSubscriptions (state, payload) {
      state.subscriptions = payload
    },
    unsubscribeFromAll (state) {
      if (state.subscriptions) {
        for (const subscription of state.subscriptions) {
          subscription.unsubscribe()
        }
      }
      state.subscriptions = null
    },
    addTeamMember (state, payload) {
      const index = state.teamMembers.findIndex(teamMembers => teamMembers.id === payload.id)
      if (index === -1) {
        state.teamMembers.push(payload)
      }
    },
    updateTeamMemberLastLogin (state, payload) {
      const teamMember = state.teamMembers.find(teamMember => teamMember.id === payload.id)
      if (teamMember) {
        teamMember.lastLogin = payload.lastLogin
      }
    },
    deleteTeamMember (state, payload) {
      const index = state.teamMembers.findIndex((teamMember) => teamMember.id === payload.id)
      if (index >= 0) {
        state.teamMembers.splice(index, 1)
      }
    }
  },
  actions: {
    async createTeamMember ({ commit }, { projectId, email }) {
      try {
        const res = await client.query({
          operationName: 'createTeamMember',
          query: CreateTeamMember,
          variables: {
            email,
            projectId
          }
        })
        commit('addTeamMember', res.createTeamMember.createTeamMemberFunction)

        return res.createTeamMember.createTeamMemberFunction
      } catch (e) {
        console.log(e)
        if (e.message.includes('already exists')) {
          alert('This team member is already added to the project.')
        } else {
          commit('snackbar/showMessage', { content: 'An error occurred while creating team member', timeout: 6000, color: 'red' }, { root: true })
        }

        return null
      }
    },

    async deleteTeamMember ({ commit }, { teamMemberId, projectId, userId, email }) {
      try {
        const res = await client.query({
          operationName: 'deleteTeamMember',
          query: DeleteTeamMember,
          variables: {
            id: teamMemberId,
            projectId,
            userId,
            email
          }
        })
        commit('deleteTeamMember', res.deleteTeamMember)
        commit('snackbar/showMessage', { content: 'Deleted' }, { root: true })
      } catch (e) {
        console.log(e)
        commit('snackbar/showMessage', { content: 'An error occurred while deleting team member', timeout: 6000, color: 'red' }, { root: true })
      }
    },

    async updateProjectOwner ({ commit }, { userId, projectId }) {
      try {
        const res = await client.query({
          operationName: 'updateProjectOwner',
          query: UpdateProjectOwner,
          context: {
            headers: {
              isTeamMember: true
            }
          },
          variables: {
            userId,
            projectId
          }
        })
        commit('projectMemberships/updateProjectOwner', res.updateProjectOwner, { root: true })
      } catch (e) {
        console.log(e)
        commit('snackbar/showMessage', { content: 'An error occurred while updating project owner', timeout: 6000, color: 'red' }, { root: true })
      }
    },

    async convertInvitesToTeamMembers ({ commit }) {
      try {
        await client.query({
          operationName: 'convertInvitesToTeamMembers',
          query: ConvertInvitesToTeamMembers
        })
      } catch (e) {
        console.log(e)
        commit('snackbar/showMessage', { content: 'An error occurred while converting invitations', timeout: 6000, color: 'red' }, { root: true })
      }
    },

    async listTeamMembers ({ commit, rootState }, { projectId }) {
      let allItems = []
      let nextToken = null

      do {
        const res = await client.query({
          operationName: 'listTeamMembers',
          query: ListTeamMembers,
          variables: { projectId, limit: 100, nextToken }
        }, {
          failSilently: true
        })
        const data = res?.listTeamMembers
        allItems = allItems.concat(data?.items || [])
        nextToken = data?.nextToken
      } while (nextToken)

      commit('setTeamMembers', allItems)

      if (rootState.isUserLoggedIn === false || rootState.workflow.isViewOnlyEnabled === true) {
        return
      }

      // Unsubscribe from previous subscriptions before creating new ones
      commit('unsubscribeFromAll')

      const subscriptions = []
      const addSubscription = (query, variables, callback) => {
        const sub = addToSubscriptions(query, variables, callback)
        subscriptions.push(sub)
      }

      addSubscription(OnCreateTeamMember, { projectId }, (data) => {
        commit('addTeamMember', data.onCreateTeamMember.createTeamMemberFunction)
      })

      addSubscription(OnUpdateTeamMemberLastLogin, { projectId }, (data) => {
        commit('updateTeamMemberLastLogin', data.onUpdateTeamMemberLastLogin)
      })

      addSubscription(OnDeleteTeamMember, { projectId }, (data) => {
        commit('deleteTeamMember', data.onDeleteTeamMember)
      })

      addSubscription(OnUpdateProjectOwner, { projectId }, (data) => {
        if (!data || !data.onUpdateProjectOwner || data.onUpdateProjectOwner.id !== projectId) return
        commit('projectMemberships/updateProjectOwner', data.onUpdateProjectOwner, { root: true })
      })

      commit('setSubscriptions', subscriptions)
    }
  }
}
